import { useRef, useState, useEffect, useCallback, type MutableRefObject } from 'react';

const LIMIT_UNIQUE_RESIZE_OBSERVATIONS = 15;

/**
 * Custom hook that observes height changes of a DOM element using ResizeObserver.
 * Tracks unique height observations and stops after reaching a limit.
 *
 * @returns Object containing containerRef to attach to a DOM element, the observed height, and any invalidation reason
 */
export const useHeightObserver = (): {
	containerRef: MutableRefObject<HTMLDivElement | null>;
	height: number | null;
	observationInvalidatedReason: string | null;
} => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [height, setHeight] = useState<number | null>(null);
	const [observationInvalidatedReason, setObservationInvalidatedReason] = useState<string | null>(
		null,
	);
	const uniqueSizeObservationsRef = useRef(0);

	// Memoize the observer callback to handle height changes
	const handleHeightChange = useCallback((newHeight: number) => {
		setHeight((prevHeight) => {
			if (prevHeight !== newHeight) {
				uniqueSizeObservationsRef.current++;
			}
			return newHeight;
		});
	}, []);

	useEffect(() => {
		if (!containerRef.current) {
			return;
		}

		// Reset observation counter for this instance
		uniqueSizeObservationsRef.current = 0;

		const observer = new ResizeObserver(([entry]) => {
			const newHeight = Math.trunc(entry.contentRect.height);
			handleHeightChange(newHeight);

			// Break after too many unique changes to prevent excessive tracking
			if (uniqueSizeObservationsRef.current >= LIMIT_UNIQUE_RESIZE_OBSERVATIONS) {
				observer.disconnect();
				setObservationInvalidatedReason(
					`Stopped counting after ${LIMIT_UNIQUE_RESIZE_OBSERVATIONS} unique resize events`,
				);
			}
		});

		observer.observe(containerRef.current);

		// Cleanup observer on unmount
		return () => observer.disconnect();
	}, [handleHeightChange]);

	return { containerRef, height, observationInvalidatedReason };
};
